import { useState } from "react";
import { Share2, Copy, MessageCircle, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const APP_URL = typeof window !== "undefined" ? window.location.origin : "https://paxmind.ai";

const MESSAGE = `I've been talking to Pax — a gentle AI companion that just listens when the day gets heavy. It's not a therapist, but it helps.\n\n${APP_URL}`;

/**
 * A compact "share Pax with a friend" card. WhatsApp-first for the Indian audience,
 * with a plain copy-link fallback. Uses Web Share API on mobile if available.
 */
export default function ShareCard() {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const openWhatsapp = () => {
        trackEvent("share_whatsapp");
        const url = `https://wa.me/?text=${encodeURIComponent(MESSAGE)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(APP_URL);
            setCopied(true);
            trackEvent("share_copy");
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("couldn't copy — try selecting manually");
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) return openWhatsapp();
        try {
            trackEvent("share_native");
            await navigator.share({ title: "Pax — a mental health companion", text: MESSAGE, url: APP_URL });
        } catch (e) { void e; }
    };

    return (
        <div className="mt-6" data-testid="share-card-wrap">
            {!open ? (
                <button
                    onClick={() => { setOpen(true); trackEvent("share_opened"); }}
                    data-testid="share-open"
                    className="w-full bg-white border border-dashed border-clayHover hover:border-terracotta rounded-3xl p-5 flex items-center gap-3 text-left transition-colors group"
                >
                    <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center">
                        <Share2 className="w-5 h-5 text-terracotta" />
                    </div>
                    <div className="flex-1">
                        <p className="font-heading text-ink text-base tracking-tight">share Pax with someone</p>
                        <p className="text-inkSoft text-xs font-body">someone in your life might need this today.</p>
                    </div>
                </button>
            ) : (
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-clayHover rounded-3xl p-5"
                        data-testid="share-card"
                    >
                        <p className="font-heading text-ink text-base tracking-tight mb-3">share Pax</p>
                        <div className="bg-clay/60 border border-clayHover rounded-2xl p-3 mb-4 text-sm text-ink font-body whitespace-pre-wrap">
                            {MESSAGE}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={openWhatsapp}
                                data-testid="share-whatsapp"
                                className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-4 py-2 font-body text-sm transition-colors"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </button>
                            <button
                                onClick={copyLink}
                                data-testid="share-copy"
                                className="flex items-center gap-2 bg-clay hover:bg-clayHover text-ink rounded-full px-4 py-2 font-body text-sm transition-colors"
                            >
                                {copied ? <Check className="w-4 h-4 text-terracotta" /> : <Copy className="w-4 h-4" />}
                                {copied ? "copied" : "copy link"}
                            </button>
                            {typeof navigator !== "undefined" && navigator.share && (
                                <button
                                    onClick={nativeShare}
                                    data-testid="share-native"
                                    className="flex items-center gap-2 bg-clay hover:bg-clayHover text-ink rounded-full px-4 py-2 font-body text-sm transition-colors"
                                >
                                    <Share2 className="w-4 h-4" /> more
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                className="text-inkSoft hover:text-ink font-body text-sm px-2"
                                data-testid="share-close"
                            >
                                close
                            </button>
                        </div>
                    </motion.div>
                </AnimatePresence>
            )}
        </div>
    );
}
